import { useEffect, Suspense } from 'react';
import { type Profile } from 'r/types';
import { unionContexts } from 'r/utils';
import { ProfileContext } from 'r/contexts';
import { default as ProfileComponent } from './Profile';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { isNewUiAtom } from 'r/atoms/newUi';
import useNewUiToggle from './useNewUiToggle';
import { AppLayout } from './Navbar';
import useIsAdmin from './useIsAdmin';
import AdminDashboard from './AdminDashboard/index';
import EventEditRoute from './AdminDashboard/EventsManagerTab/EventEditRoute';
import ScoreMarketEditRoute from './AdminDashboard/ScoreMarketsTab/ScoreMarketEditRoute';
import AnalysisEditRoute from './AdminDashboard/AnalysisManagerTab/AnalysisEditRoute';
import AnalysisPlayEventRoute from './AdminDashboard/AnalysisManagerTab/AnalysisPlayEventRoute';
import AnalysisScoreMarketRoute from './AdminDashboard/AnalysisManagerTab/AnalysisScoreMarketRoute';
import KolPage from './AdminDashboard/KolsTab/KolPage';
import AdminHistoryEditRoute from './AdminDashboard/AdminHistory/AdminHistoryEditRoute';

export default unionContexts(App, [ProfileContext, 'profile', 'auth']);
function App({ profile, auth }: { profile: Profile; auth: () => void }) {
  useIsAdmin();
  useNewUiToggle();
  const isNewUi = useAtomValue(isNewUiAtom);

  const location = useLocation();
  const navigate = useNavigate();

  // For modal routes without an explicit background, derive one from the path
  const background =
    location.state?.background ??
    (location.pathname.startsWith('/score-markets/') ?
      { pathname: '/', search: '?tab=score-markets' }
    : location.pathname.startsWith('/events/') ?
      { pathname: '/', search: '?tab=events' }
    : location.pathname.startsWith('/analyses/') ?
      { pathname: '/', search: '?tab=analyses' }
    : location.pathname.startsWith('/admin-history/') ?
      { pathname: '/', search: '?tab=history' }
    : null);

  useEffect(() => {
    if (location.pathname !== '/' && location.pathname.slice(-1)[0] === '/') {
      navigate(
        `${location.pathname.slice(0, -1)}${location.search}${location.hash}`,
        { state: location.state, replace: true },
      );
      return;
    }
    if (!profile && location.pathname !== '/') {
      navigate(`/${location.search}${location.hash}`, {
        state: {
          forward: `${location.pathname}${location.search}${location.hash}`,
        },
      });
    }
  }, [location, profile]);

  return (
    <div className="bg-background min-h-dvh relative">
      <div className="w-full flex">
        <div className="relative flex w-full">
          <AppLayout onSignIn={() => auth()} profile={profile}>
            <div className="flex w-full justify-center">
              <Routes location={background || location}>
                <Route
                  element={
                    <Suspense fallback={null}>
                      <AdminDashboard />
                    </Suspense>
                  }
                  path="/"
                />
                <Route
                  element={
                    <Suspense fallback={null}>
                      {profile && <ProfileComponent />}
                    </Suspense>
                  }
                  path="/profile/"
                />
                <Route
                  element={
                    isNewUi ?
                      <Suspense fallback={null}>
                        <KolPage />
                      </Suspense>
                    : <Navigate to="/" replace />
                  }
                  path="/kols/:kolId"
                />
              </Routes>

              {/* Modal routes — rendered on top of background */}
              <Routes>
                <Route
                  path="/events/:eventId/edit"
                  element={<EventEditRoute />}
                />
                <Route
                  path="/score-markets/:marketId/edit"
                  element={<ScoreMarketEditRoute />}
                />
                <Route
                  path="/analyses/:analysisId/edit"
                  element={<AnalysisEditRoute />}
                />
                <Route
                  path="/analyses/:analysisId/play-event"
                  element={<AnalysisPlayEventRoute />}
                />
                <Route
                  path="/analyses/:analysisId/score-market"
                  element={<AnalysisScoreMarketRoute />}
                />
                <Route
                  path="/admin-history/:analysisId/edit"
                  element={<AdminHistoryEditRoute />}
                />
              </Routes>
            </div>
          </AppLayout>
        </div>
      </div>
    </div>
  );
}
